import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Field, makeStyles, tokens, Tooltip } from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { CalendarMonthRegular, InfoRegular } from "@fluentui/react-icons";
import moment from "moment";

const useStyles = makeStyles({
  datePicker: {
    width: "100%",
    padding: "6px 10px",
    paddingRight: "32px",
    border: "none",
    backgroundColor: "white",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    "&:focus": {
      outline: "none",
    },
    "&:disabled": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  compactField: {
    "& .fui-Field__labelWrapper": {
      marginBottom: "2px",
    },
    "& .fui-Field__validationMessage": {
      marginTop: "2px",
    },
  },
  clearButton: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "22px",
    fontWeight: "bold",
    opacity: 0.4,
    color: tokens.colorNeutralForeground3,
    padding: "0 4px",
    height: "20px",
    width: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  container: {
    position: "relative",
    width: "100%",
  },
  calendarIcon: {
    position: "absolute",
    right: "8px",
    top: "55%",
    transform: "translateY(-50%)",
    color: tokens.colorNeutralForeground3,
    pointerEvents: "none",
    zIndex: 2,
  },
  infoIcon: {
    position: "absolute",
    right: "36px",
    top: "55%",
    transform: "translateY(-50%)",
    color: tokens.colorNeutralForeground3,
    cursor: "help",
    zIndex: 2,
    fontSize: "16px",
    "&:hover": {
      color: tokens.colorNeutralForeground2,
    },
  },
  calendarWrapper: {
    "& .react-datepicker": {
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase200,
      backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      boxShadow: tokens.shadow8,
    },
    "& .react-datepicker__header": {
      backgroundColor: tokens.colorNeutralBackground1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
      padding: "8px 0 4px",
    },
    "& .react-datepicker__current-month": {
      color: tokens.colorNeutralForeground1,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      marginBottom: "4px",
    },
    "& .react-datepicker__day-name": {
      color: tokens.colorNeutralForeground3,
      fontSize: tokens.fontSizeBase100,
      fontWeight: tokens.fontWeightRegular,
      width: "28px",
      lineHeight: "20px",
      margin: "0 1px",
    },
    "& .react-datepicker__month": {
      margin: "4px 8px",
    },
    "& .react-datepicker__day": {
      color: tokens.colorNeutralForeground1,
      fontSize: tokens.fontSizeBase200,
      width: "28px",
      height: "28px",
      lineHeight: "28px",
      borderRadius: tokens.borderRadiusSmall,
      margin: "1px",
      "&:hover": {
        backgroundColor: tokens.colorNeutralBackground1Hover,
      },
    },
    "& .react-datepicker__day--selected": {
      backgroundColor: `${tokens.colorBrandBackground} !important`,
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
      borderRadius: tokens.borderRadiusSmall,
      "&:hover": {
        backgroundColor: tokens.colorBrandBackgroundHover,
      },
    },
    "& .react-datepicker__day--today": {
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorBrandForeground1,
    },
    "& .react-datepicker__day--keyboard-selected": {
      backgroundColor: `${tokens.colorBrandBackground} !important`,
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
    },
    "& .react-datepicker__day--outside-month": {
      color: tokens.colorNeutralForeground4,
    },
    "& .react-datepicker__day--disabled": {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "& .react-datepicker__navigation": {
      top: "12px",
      width: "24px",
      height: "24px",
      "&:hover": {
        backgroundColor: tokens.colorNeutralBackground1Hover,
        borderRadius: tokens.borderRadiusSmall,
      },
    },
    "& .react-datepicker__triangle": {
      display: "none",
    },
    "& .react-datepicker__input-container input": {
      backgroundColor: "white",
      border: "1px solid #cdcdcd",
      width: "100%",
      fontSize: tokens.fontSizeBase200,
      "&:focus": {
        outline: "none",
      },
    },
    "& .react-datepicker__time-container": {
      borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
      width: "80px",
    },
    "& .react-datepicker__time": {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    "& .react-datepicker__header--time": {
      backgroundColor: tokens.colorNeutralBackground1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
      padding: "8px 4px",
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightSemibold,
    },
    "& .react-datepicker__time-list-item": {
      height: "auto !important",
      padding: "5px 8px !important",
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground1,
      "&:hover": {
        backgroundColor: `${tokens.colorNeutralBackground1Hover} !important`,
      },
    },
    "& .react-datepicker__time-list-item--selected": {
      backgroundColor: `${tokens.colorBrandBackground} !important`,
      color: `${tokens.colorNeutralForegroundOnBrand} !important`,
      "&:hover": {
        backgroundColor: `${tokens.colorBrandBackgroundHover} !important`,
      },
    },
  },
  calendarWrapperWithBorder: {
    "& .react-datepicker__input-container input": {
      backgroundColor: "white",
      border: "1px solid #cdcdcd",
      borderRadius: tokens.borderRadiusMedium,
      width: "100%",
      fontSize: tokens.fontSizeBase200,
      "&:focus": {
        outline: `1px solid ${tokens.colorBrandStroke1}`,
      },
    },
  },
  yearSelect: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: "2px 6px",
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    marginLeft: "4px",
    cursor: "pointer",
    height: "28px",
    "&:focus": {
      outline: `1px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "1px",
    },
  },
  monthSelect: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: "2px 6px",
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    cursor: "pointer",
    height: "28px",
    "&:focus": {
      outline: `1px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "1px",
    },
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 8px",
    gap: "4px",
  },
  monthYearContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  navigationContainer: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  navButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    borderRadius: tokens.borderRadiusSmall,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    minWidth: "24px",
    minHeight: "24px",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    "&:disabled": {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  },
  tooltipContent: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: "1.4",
    maxWidth: "200px",
    padding: "4px 8px",
  },
});

export default function DynamicDateTimePicker({
  formik,
  name,
  disabled,
  customHandleChange,
  label,
  required,
  border = false,
  width = "100%",
}: any) {
  const styles = useStyles();
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    formik.values[name] ? new Date(formik.values[name]) : null,
  );
  const [isBackspace, setIsBackspace] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showFormatTooltip, setShowFormatTooltip] = useState(false);
  const calendarWrapper = border
    ? styles.calendarWrapperWithBorder
    : styles.calendarWrapper;

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    const formatted = date ? moment(date).format("YYYY-MM-DDTHH:mm") : null;
    formik.setFieldValue(name, formatted);
    formik.setFieldError(name, undefined);
    if (customHandleChange !== undefined) {
      customHandleChange({
        target: { name: name, value: formatted },
      });
    }
  };

  useEffect(() => {
    const value = formik.values[name];
    setSelectedDate(value ? new Date(value) : null);
  }, [formik.values[name]]);

  const years = Array.from(
    { length: 111 },
    (_, i) => new Date().getFullYear() - 100 + i,
  );
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return (
    <Field
      label={
        <span>
          {label}
          {required && <span className="required-item">*</span>}
        </span>
      }
      validationState={
        formik.errors[name] && formik.touched[name] ? "error" : "none"
      }
      validationMessage={
        formik.errors[name] && formik.touched[name] ? formik.errors[name] : undefined
      }
      style={{ width, marginTop: "-4px" }}
    >
      <div className={`${styles.container} ${styles.calendarWrapper}`}>
        <DatePicker
          selected={selectedDate}
          onInputClick={() => setIsOpen(true)}
          open={isOpen}
          onClickOutside={() => setIsOpen(false)}
          onSelect={(date) => {
            handleDateChange(date);
          }}
          onChange={(date) => {
            handleDateChange(date);
          }}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={30}
          timeCaption="Time"
          dateFormat="dd/MM/yyyy HH:mm"
          placeholderText="DD/MM/YYYY HH:mm"
          className={styles.datePicker}
          disabled={disabled}
          showPopperArrow={false}
          popperClassName={styles.calendarWrapper}
          // Enforces a fixed viewport strategy context instead of absolute offset coordinates
          popperProps={{ strategy: "fixed" } as any}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Backspace") {
              setIsBackspace(true);
            } else {
              setIsBackspace(false);
            }
            const allowed = [
              "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter",
            ];
            if (/^[0-9]$/.test(e.key)) return;
            if (allowed.includes(e.key)) return;
            e.preventDefault();
          }}
          onChangeRaw={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.target?.value && e.target?.value !== "") {
              return;
            }
            const value = e.target.value;
            if (value === undefined || value === null) return;

            let v = value.replace(/\D/g, "");
            if (v.length > 8) v = v.slice(0, 8);

            if (v.length >= 1) {
              const d1 = Number(v[0]);
              if (d1 > 3) v = "3";
            }
            if (v.length >= 2) {
              const day = Number(v.slice(0, 2));
              if (day < 1) v = "01" + v.slice(2);
              if (day > 31) v = "31" + v.slice(2);
            }
            if (v.length >= 3) {
              const m1 = Number(v[2]);
              if (m1 > 1) v = v.slice(0, 2) + "1";
            }
            if (v.length >= 4) {
              const month = Number(v.slice(2, 4));
              if (month < 1) v = v.slice(0, 2) + "01" + v.slice(4);
              if (month > 12) v = v.slice(0, 2) + "12" + v.slice(4);
            }

            let f = "";
            if (v.length >= 2) {
              f = v.slice(0, 2) + "/";
            } else {
              f = v;
            }
            if (v.length >= 4) {
              f += v.slice(2, 4) + "/";
            } else if (v.length > 2) {
              f += v.slice(2);
            }
            if (v.length > 4) {
              f += v.slice(4);
            }

            e.target.value = f;
            if (isBackspace) {
              e.target.value.endsWith("/") && (e.target.value = e.target.value.slice(0, -1));
              setIsBackspace(false);
            }

            if (f.length === 10) {
              const [dd, mm, yyyy] = f.split("/");
              const date = new Date(`${yyyy}-${mm}-${dd}`);
              if (!isNaN(date.getTime())) {
                formik.setFieldValue(name, moment(date).format("YYYY-MM-DDTHH:mm"));
                formik.setFieldError(name, undefined);
              }
            }
          }}
          popperModifiers={[
            {
              name: "offset",
              options: { offset: [0, 4] },
            },
            {
              name: "clippingParents",
            },
            // Modifies style injection block during evaluation phase to override underlying UI tables
            {
              name: "setZIndex",
              enabled: true,
              phase: "write",
              order: 999,
              fn: ({ state }: any) => {
                state.elements.popper.style.zIndex = "9999";
              },
            } as any,
          ]}
          calendarClassName={styles.calendarWrapper}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
            changeYear,
            changeMonth,
          }) => (
            <div className={styles.headerContainer}>
              <div className={styles.navigationContainer}>
                <button
                  type="button"
                  onClick={() => changeYear(date.getFullYear() - 1)}
                  className={styles.navButton}
                  aria-label="Previous year"
                >
                  ‹‹
                </button>
                <button
                  type="button"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  className={styles.navButton}
                  aria-label="Previous month"
                >
                  ‹
                </button>
              </div>

              <div className={styles.monthYearContainer}>
                <select
                  className={styles.monthSelect}
                  value={months[date.getMonth()]}
                  onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                >
                  {months.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>

                <select
                  className={styles.yearSelect}
                  value={date.getFullYear()}
                  onChange={({ target: { value } }) => changeYear(Number(value))}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className={styles.navigationContainer}>
                <button
                  type="button"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  className={styles.navButton}
                  aria-label="Next month"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() => changeYear(date.getFullYear() + 1)}
                  className={styles.navButton}
                  aria-label="Next year"
                >
                  ››
                </button>
              </div>
            </div>
          )}
        />

        {!disabled && (
          <>
            <Tooltip
              relationship="label"
              content={
                <div className={styles.tooltipContent}>
                  <strong>Date &amp; Time Format: DD/MM/YYYY HH:mm</strong>
                  <div style={{ marginTop: "4px" }}>
                    Type the date digits to auto-format, then pick a time from the calendar.
                    <br />
                    Example: 15012024 becomes 15/01/2024
                  </div>
                </div>
              }
              withArrow
              positioning="above-start"
              showDelay={300}
              hideDelay={0}
            >
              <div
                className={styles.infoIcon}
                onMouseEnter={() => setShowFormatTooltip(true)}
                onMouseLeave={() => setShowFormatTooltip(false)}
              >
                <InfoRegular />
              </div>
            </Tooltip>

            {selectedDate ? (
              <button
                type="button"
                className={styles.clearButton}
                onClick={() => {
                  setSelectedDate(null);
                  formik.setFieldValue(name, null);
                }}
                aria-label="Clear date"
              >
                ×
              </button>
            ) : (
              <div className={styles.calendarIcon}>
                <CalendarMonthRegular fontSize={22} />
              </div>
            )}
          </>
        )}
      </div>
    </Field>
  );
}